// Local-market pricing + localized number/currency rendering for the website.
//
// The gateway owns the price book (api/src/services/currencyPricing.ts):
// every market pays its own multiplex-normal price in its own money. This
// module fetches that card once per country, and formats every money amount
// and digit in the viewer's own numbering system (Intl handles Devanagari,
// Arabic-Indic, Bengali... digits from the locale alone).
const API_BASE = 'https://api.starviewcinema.com';

// Stripe zero-decimal currencies: the integer amount IS the major unit.
const ZERO_DECIMAL = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF',
  'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF', 'ISK', 'HUF', 'TWD',
]);

export function stripeAmountToMajor(amount, currency) {
  return ZERO_DECIMAL.has(String(currency || 'USD').toUpperCase()) ? amount : amount / 100;
}

// Representative formatting locale per platform country name - drives
// currency style, digit shapes, and separators even where the UI language
// falls back to English.
const COUNTRY_FORMAT_LOCALE = {
  USA: 'en-US', CANADA: 'en-CA', UK: 'en-GB', AUSTRALIA: 'en-AU',
  INDIA: 'hi-IN', CHINA: 'zh-CN', TAIWAN: 'zh-TW', HONGKONG: 'zh-HK',
  JAPAN: 'ja-JP', KOREA: 'ko-KR', MEXICO: 'es-MX', SPAIN: 'es-ES',
  FRANCE: 'fr-FR', GERMANY: 'de-DE', AUSTRIA: 'de-AT', SWITZERLAND: 'de-CH',
  UKRAINE: 'uk-UA', ITALY: 'it-IT', PORTUGAL: 'pt-PT', BRAZIL: 'pt-BR',
  NETHERLANDS: 'nl-NL', BELGIUM: 'nl-BE', POLAND: 'pl-PL', CZECHIA: 'cs-CZ',
  SLOVAKIA: 'sk-SK', HUNGARY: 'hu-HU', ROMANIA: 'ro-RO', BULGARIA: 'bg-BG',
  GREECE: 'el-GR', TURKEY: 'tr-TR', RUSSIA: 'ru-RU', BELARUS: 'ru-BY',
  SWEDEN: 'sv-SE', NORWAY: 'nb-NO', DENMARK: 'da-DK', FINLAND: 'fi-FI',
  ICELAND: 'is-IS', IRELAND: 'en-IE', CROATIA: 'hr-HR', SERBIA: 'sr-RS',
  SLOVENIA: 'sl-SI', ESTONIA: 'et-EE', LATVIA: 'lv-LV', LITHUANIA: 'lt-LT',
  ISRAEL: 'he-IL', SAUDIARABIA: 'ar-SA', UAE: 'ar-AE', QATAR: 'ar-QA',
  KUWAIT: 'ar-KW', BAHRAIN: 'ar-BH', OMAN: 'ar-OM', JORDAN: 'ar-JO',
  LEBANON: 'ar-LB', IRAQ: 'ar-IQ', SYRIA: 'ar-SY', YEMEN: 'ar-YE',
  PALESTINE: 'ar-PS', EGYPT: 'ar-EG', MOROCCO: 'ar-MA', ALGERIA: 'ar-DZ',
  TUNISIA: 'ar-TN', LIBYA: 'ar-LY', SUDAN: 'ar-SD', IRAN: 'fa-IR',
  AFGHANISTAN: 'fa-AF', PAKISTAN: 'ur-PK', BANGLADESH: 'bn-BD',
  SRILANKA: 'si-LK', NEPAL: 'ne-NP', MYANMAR: 'my-MM', THAILAND: 'th-TH',
  VIETNAM: 'vi-VN', INDONESIA: 'id-ID', MALAYSIA: 'ms-MY',
  PHILIPPINES: 'fil-PH', CAMBODIA: 'km-KH', LAOS: 'lo-LA',
  MONGOLIA: 'mn-MN', KAZAKHSTAN: 'kk-KZ', UZBEKISTAN: 'uz-UZ',
  GEORGIA: 'ka-GE', ARMENIA: 'hy-AM', AZERBAIJAN: 'az-AZ',
  ETHIOPIA: 'am-ET', KENYA: 'sw-KE', TANZANIA: 'sw-TZ', NIGERIA: 'en-NG',
  AFRICA: 'en-NG', GHANA: 'en-GH', SOUTHAFRICA: 'en-ZA', SENEGAL: 'fr-SN',
  IVORYCOAST: 'fr-CI', CAMEROON: 'fr-CM', ARGENTINA: 'es-AR', CHILE: 'es-CL',
  COLOMBIA: 'es-CO', PERU: 'es-PE', VENEZUELA: 'es-VE', ECUADOR: 'es-EC',
  BOLIVIA: 'es-BO', PARAGUAY: 'es-PY', URUGUAY: 'es-UY', GUATEMALA: 'es-GT',
  HONDURAS: 'es-HN', ELSALVADOR: 'es-SV', NICARAGUA: 'es-NI',
  COSTARICA: 'es-CR', PANAMA: 'es-PA', CUBA: 'es-CU',
  DOMINICANREPUBLIC: 'es-DO', SINGAPORE: 'en-SG', NEWZEALAND: 'en-NZ',
};

export function localeForCountry(countryName) {
  return COUNTRY_FORMAT_LOCALE[String(countryName || '').toUpperCase().replace(/\s+/g, '')] || 'en-US';
}

export function formatMoney(amountMajor, currency, countryName) {
  try {
    return new Intl.NumberFormat(localeForCountry(countryName), {
      style: 'currency',
      currency: String(currency || 'USD').toUpperCase(),
      maximumFractionDigits: ZERO_DECIMAL.has(String(currency).toUpperCase()) ? 0 : 2,
    }).format(amountMajor);
  } catch {
    return `${currency ? String(currency).toUpperCase() + ' ' : '$'}${Number(amountMajor).toFixed(2)}`;
  }
}

// Localize the DIGITS inside any string (seat "A12", slot "11:45 PM") into
// the country's numbering system - e.g. Devanagari in India, Arabic-Indic
// across MENA. Latin-digit locales pass through unchanged.
const digitCache = new Map();
export function localizeDigits(text, countryName) {
  const locale = localeForCountry(countryName);
  let digits = digitCache.get(locale);
  if (!digits) {
    try {
      const nf = new Intl.NumberFormat(locale, { useGrouping: false });
      digits = [...Array(10)].map((_, i) => nf.format(i));
    } catch {
      digits = null;
    }
    digitCache.set(locale, digits);
  }
  if (!digits || digits[0] === '0') return String(text);
  return String(text).replace(/[0-9]/g, (d) => digits[Number(d)]);
}

// One local price card per country, cached for the session.
const priceCache = new Map();
export async function getLocalPrice(countryName) {
  const key = String(countryName || 'USA').toUpperCase();
  if (priceCache.has(key)) return priceCache.get(key);
  try {
    const res = await fetch(`${API_BASE}/api/tickets/local-price?country=${encodeURIComponent(key)}`);
    if (!res.ok) return null;
    const card = await res.json();
    priceCache.set(key, card);
    return card;
  } catch {
    return null;
  }
}
